import React from 'react';

import { Box, Grid, GridProps, Typography } from '@mui/material';
import { FieldState, FormApi } from 'final-form';
import { FormSpy } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';

import { Button } from 'src/components/common';
import type { SxProp } from 'src/types/mui';

export function FieldArrayTemplate<FieldValue = any, T extends HTMLElement = HTMLElement>(
  props: FieldArrayTemplateProps<FieldValue, T>,
) {
  const {
    name: fieldArrayName,
    label,
    children,
    onAdd,
    onRemove,
    addIcon,
    containerProps,
    fieldItemStyle,
  } = props;

  return (
    <FormSpy subscription={{ submitting: true }}>
      {({ submitting, form: { getFieldState } }) => (
        <FieldArray
          name={fieldArrayName}
          render={({ fields }) => (
            <Grid container spacing={3} {...containerProps}>
              {fields.map((name, index) => (
                <Grid key={name} size={{ xs: 12 }}>
                  <Box component="section" sx={fieldItemStyle || fieldArrayItemStyles}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography component="h4" variant="body1">
                        {renderLabel(label, index)}
                      </Typography>
                      <Button
                        disabled={Boolean(fields.length && fields.length <= 1) || submitting}
                        onClick={() => onRemove(fields, index, name)}
                        color="error"
                        variant="outlined"
                        size="small"
                      >
                        Delete
                      </Button>
                    </Box>
                    {children({
                      fields,
                      index,
                      name,
                      renderFieldName: renderFieldName(name),
                      getArrayFieldState: getArrayFieldState(getFieldState, name),
                    })}
                  </Box>
                </Grid>
              ))}
              <Grid size={{ xs: 12 }}>
                <Box justifyContent="flex-end" alignItems="center" display="flex" width="100%">
                  <Button startIcon={addIcon} onClick={() => onAdd(fields)} disabled={submitting}>
                    Add another
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        />
      )}
    </FormSpy>
  );
}

const renderLabel = (label: string, index: number) => `${label} #${index + 1}`;

const renderFieldName = (name: string) => (fieldName: string) => `${name}.${fieldName}`;

const getArrayFieldState =
  (getFieldState: FormApi['getFieldState'], name: string) => (fieldName: string) =>
    getFieldState(renderFieldName(name)(fieldName));

export interface FieldArrayRenderItemProps<FieldValue, T extends HTMLElement> {
  name: string;
  index: number;
  renderFieldName: (fieldName: string) => string;
  getArrayFieldState: (fieldName: string) => FieldState<FieldValue> | undefined;
  fields: FieldArrayRenderProps<FieldValue, T>['fields'];
}

export interface FieldArrayTemplateProps<FieldValue, T extends HTMLElement> {
  label: string;
  name: string;
  children: (props: FieldArrayRenderItemProps<FieldValue, T>) => React.ReactElement;
  addIcon: React.ReactElement;
  onAdd: (fields: FieldArrayRenderProps<FieldValue, T>['fields']) => void;
  onRemove: (
    fields: FieldArrayRenderProps<FieldValue, T>['fields'],
    index: number,
    name: string,
  ) => void;
  containerProps?: Partial<GridProps>;
  fieldItemStyle?: SxProp;
}

const fieldArrayItemStyles: SxProp = {
  p: 3,
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderRadius: '8px',
  width: '100%',
};
